import "@/App.css";
import "@/styles/tokens.css";
import { useEffect, useState, lazy, Suspense } from "react";
import api from "@/lib/api";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AccessDenied, NotFound } from "@/components/app/RouteStates";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LocaleProvider } from "@/context/LocaleContext";
import { PreviewProvider } from "@/context/PreviewContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/app/ErrorBoundary";
import ScrollToTop from "@/components/app/ScrollToTop";

import AppShell from "@/layouts/AppShell";
import SpacesShell from "@/layouts/SpacesShell";
import OsShell from "@/layouts/OsShell";
import Login from "@/pages/LoginExecutive";
import LoginLegacy from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import LoginPreview from "@/pages/login-preview/LoginPreview";
import Dashboard from "@/pages/Dashboard";
import OsHome from "@/pages/os/OsHome";
import EnterpriseHome from "@/pages/home/EnterpriseHome";
import CommandCentreHome from "@/pages/home/CommandCentreHome";
import VisualDNA from "@/pages/brand/VisualDNA";
import PartnerList from "@/pages/partners/PartnerList";
import ActionCentre from "@/pages/actioncentre/ActionCentre";
import RoleDashboard from "@/pages/dashboards/RoleDashboard";
import DashboardAdmin from "@/pages/dashboards/DashboardAdmin";
import Customer360 from "@/pages/customer360/Customer360";
import LifecyclePage from "@/pages/lifecycle/LifecyclePage";
import CopilotPage from "@/pages/copilot/CopilotPage";
import AutomationEngine from "@/pages/automation/AutomationEngine";
import ExceptionCentre from "@/pages/exceptions/ExceptionCentre";
import NotificationCentrePage from "@/pages/notifications/NotificationCentrePage";
import CommandCentre from "@/pages/command/CommandCentre";
import IntegrationHub from "@/pages/admin/IntegrationHub";
import WhatsAppIntegration from "@/pages/admin/WhatsAppIntegration";
import Assets from "@/pages/Assets";
import Requests from "@/pages/Requests";
import Approvals from "@/pages/Approvals";
import DeployDevice from "@/pages/DeployDevice";
import OurStockAtSuntronix from "@/pages/customer360/OurStockAtSuntronix";
import Shipments from "@/pages/Shipments";
import ReversePickup from "@/pages/ReversePickup";
import Inspection from "@/pages/Inspection";
import DataErasure from "@/pages/DataErasure";
import ITAD from "@/pages/ITAD";
import Employees from "@/pages/Employees";
import Companies, { CompanySubsidiaries } from "@/pages/Companies";
import ImportHistory from "@/pages/admin/ImportHistory";
import ForceOverrides from "@/pages/admin/ForceOverrides";
import ApprovalsInbox from "@/pages/ApprovalsInbox";
import ScanAsset from "@/pages/ScanAsset";
import Catalogue from "@/pages/Catalogue";
import ServiceCatalogue from "@/pages/ServiceCatalogue";
import ServiceGroup from "@/pages/ServiceGroup";
import ServiceDetail from "@/pages/ServiceDetail";
import ServiceBundle from "@/pages/ServiceBundle";
import ServiceRequestWizard from "@/pages/ServiceRequestWizard";
import ServiceAnalytics from "@/pages/admin/ServiceAnalytics";
import Users from "@/pages/Users";
import Audit from "@/pages/Audit";
import AuditTrail from "@/pages/audit/AuditTrail";
import Exceptions from "@/pages/Exceptions";
import Service from "@/pages/Service";
import Warehouse from "@/pages/Warehouse";
import Offboarding from "@/pages/Offboarding";
import Reports from "@/pages/Reports";
import ImportCentre from "@/pages/ImportCentre";
import RefreshForecast from "@/pages/RefreshForecast";
import MyDevices from "@/pages/MyDevices";
import Connectors from "@/pages/Connectors";
import DiscoveryInbox from "@/pages/DiscoveryInbox";
import Discrepancies from "@/pages/Discrepancies";
import Duplicates from "@/pages/Duplicates";
import Normalization from "@/pages/Normalization";
import ProductIntelligence from "@/pages/ProductIntelligence";
import DataQuality from "@/pages/DataQuality";
import DataQualityControl from "@/pages/admin/DataQualityControl";
import CatalogueMasters from "@/pages/admin/CatalogueMasters";
import CatalogueCleanup from "@/pages/admin/CatalogueCleanup";
import ProductClassification from "@/pages/admin/ProductClassification";
import MappingReview from "@/pages/admin/MappingReview";
import MultiBrandStock from "@/pages/operations/MultiBrandStock";
import ProductContentProviders from "@/pages/admin/ProductContentProviders";
import { WaComposerHost } from "@/components/whatsapp/WaComposer";
import ProductContentHub from "@/pages/technology/ProductContentHub";
import MyOrders from "@/pages/customer/MyOrders";
import AdminExceptions from "@/pages/admin/AdminExceptions";
import IntegrationHealth from "@/pages/admin/IntegrationHealth";
import SystemHealth from "@/pages/admin/SystemHealth";
import FailureCentre from "@/pages/admin/FailureCentre";
import AutomationStudio from "@/pages/atlas/AutomationStudio";
import FlowDefinitions from "@/pages/atlas/FlowDefinitions";
import RequestHubLanding from "@/pages/RequestHubLanding";
import RequestHubPage from "@/pages/RequestHubPage";
import SecureVault from "@/pages/admin/vault/SecureVault";
import KycEngine from "@/pages/compliance/KycEngine";
import KycReadiness from "@/pages/compliance/KycReadiness";
import HsnFormatTool from "@/pages/tools/HsnFormatTool";
import ImageFormatTool from "@/pages/tools/ImageFormatTool";
import ProductFormatCentre from "@/pages/tools/ProductFormatCentre";
import AppleCTO from "@/pages/commercial/AppleCTO";
import ArWhatsAppDesk from "@/pages/finance/ArWhatsAppDesk";
import DemoConsole from "@/pages/admin/DemoConsole";
import TenantReadiness from "@/pages/admin/TenantReadiness";
import PreflightRules from "@/pages/admin/PreflightRules";
import InvoiceCentre from "@/pages/InvoiceCentre";
import OrderClosure from "@/pages/finance/OrderClosure";
import AddedItemsReport from "@/pages/reports/AddedItemsReport";
import InvoiceCopies from "@/pages/finance/InvoiceCopies";
import Advisories from "@/pages/Advisories";
import InventoryAudit from "@/pages/InventoryAudit";
import SourcePriority from "@/pages/SourcePriority";
import Reservations from "@/pages/Reservations";
import Loaners from "@/pages/Loaners";
import RMA from "@/pages/RMA";
import Warranty from "@/pages/Warranty";
import WarrantyNudges from "@/pages/WarrantyNudges";
import RefreshEngine from "@/pages/RefreshEngine";
import CustomerHome from "@/pages/CustomerHome";
import EmployeeDetail from "@/pages/EmployeeDetail";
import HelpCentre from "@/pages/help/HelpCentre";
import TrustCentre from "@/pages/help/TrustCentre";
import AccessibilityStatement from "@/pages/help/AccessibilityStatement";
import SystemStatus from "@/pages/help/SystemStatus";
import ExecutiveHome from "@/pages/ExecutiveHome";
import Concierge from "@/pages/Concierge";
import WhatsNew from "@/pages/WhatsNew";
import Notices from "@/pages/Notices";
import Feedback from "@/pages/Feedback";
import ProductFeedback from "@/pages/feedback/ProductFeedback";
import Roadmap from "@/pages/roadmap/Roadmap";
import Conversations from "@/pages/Conversations";
import Mentions from "@/pages/Mentions";
import NavigationPreview from "@/pages/preview/NavigationPreview";
import MyInventory from "@/pages/MyInventory";
import BulkInward from "@/pages/BulkInward";
import DevicePassport from "@/pages/DevicePassport";
import TechnologyEnvironment from "@/pages/customer360/TechnologyEnvironment";
import CustomerMatrix from "@/pages/customer360/CustomerMatrix";
import MasterCustomersList from "@/pages/customer360/MasterCustomersList";
import MasterCustomer360 from "@/pages/customer360/MasterCustomer360";
import OnboardingWizard from "@/pages/customer360/OnboardingWizard";
import TaxRegistrationsEditor from "@/pages/customer360/TaxRegistrationsEditor";
import ContractsEditor from "@/pages/customer360/ContractsEditor";
import LogisticsConsole from "@/pages/customer360/LogisticsConsole";
import BulkDeployment from "@/pages/customer360/BulkDeployment";
import DeploymentControlCentre from "@/pages/customer360/DeploymentControlCentre";
import PreAcceptanceDelivery from "@/pages/customer360/PreAcceptanceDelivery";
import PublicDeliveryConfirm from "@/pages/PublicDeliveryConfirm";
import PublicQuoteAccept from "@/pages/PublicQuoteAccept";
import DigitalQuote from "@/pages/public/DigitalQuote";
import PublicProductIntelligence from "@/pages/PublicProductIntelligence";
import PublicOneForm from "@/pages/forms/PublicOneForm";
import TenantCatalogueAdmin from "@/pages/admin/TenantCatalogueAdmin";
import TenantCatalogue from "@/pages/catalogue/TenantCatalogue";
import TenantMyQuotes from "@/pages/catalogue/MyQuotes";
import TenantHome from "@/pages/catalogue/TenantHome";
import TenantMyOrders from "@/pages/catalogue/MyOrders";
import TenantMyAssets from "@/pages/catalogue/MyAssets";
import TenantMyInvoices from "@/pages/catalogue/MyInvoices";
import TenantMyLocations from "@/pages/catalogue/MyLocations";import PublicFeedback from "@/pages/PublicFeedback";
import DeploymentCase from "@/pages/customer360/DeploymentCase";
import MFASettings from "@/pages/mfa/MFASettings";
import MasterClientPortfolio from "@/pages/customer360/MasterClientPortfolio";
import GovernanceConsole from "@/pages/governance/GovernanceConsole";
import GovernanceCentre from "@/pages/governance/GovernanceCentre";
import ControlTower from "@/pages/operations/ControlTower";
import Marketplace from "@/pages/marketplace/Marketplace";
import MarketplaceAdmin from "@/pages/marketplace/MarketplaceAdmin";
import PartnerNetwork from "@/pages/partners/PartnerNetwork";
import ScenarioLab from "@/pages/insights/ScenarioLab";
import Economics from "@/pages/insights/Economics";
import Circularity from "@/pages/insights/Circularity";
import Studio from "@/pages/admin/Studio";
import LocationMaster from "@/pages/LocationMaster";
import AdminControlCentre from "@/pages/admin/AdminControlCentre";
import LockedAccounts from "@/pages/admin/LockedAccounts";
import OneSms from "@/pages/admin/OneSms";
import FinanceVault from "@/pages/admin/FinanceVault";
import OneContinuity from "@/pages/myone/OneContinuity";
import OneToday from "@/pages/myone/OneToday";
import OneAiWorkspace from "@/pages/oneai/OneAiWorkspace";
import OneServiceDesk from "@/pages/service/OneServiceDesk";
import EmployeeHub from "@/pages/hubs/EmployeeHub";
import ServiceHub from "@/pages/hubs/ServiceHub";
import CommandCentreHub from "@/pages/hubs/CommandCentre";
import ExperienceSettings from "@/pages/ExperienceSettings";
import OneControlCentre from "@/pages/admin/OneControlCentre";
import OneDocumentVault from "@/pages/vault/OneDocumentVault";
import OneInbox from "@/pages/vault/OneInbox";
import VaultSharePublic from "@/pages/vault/VaultSharePublic";
import PrivateVault from "@/pages/privatevault/PrivateVault";
import AiOps from "@/pages/aiops/AiOps";
import WarrantyIntelligence from "@/pages/warranty/WarrantyIntelligence";
import CioCommandCenter from "@/pages/cio/CioCommandCenter";
import AskAiPage from "@/pages/askai/AskAiPage";
import AlertCentre from "@/pages/alerts/AlertCentre";
import AiRecommendations from "@/pages/recommendations/AiRecommendations";
import IntegrationCentre from "@/pages/admin/IntegrationCentre";
import AlertPolicies from "@/pages/alerts/AlertPolicies";
import PrivateVaultShare from "@/pages/privatevault/PrivateVaultShare";
import WhatsAppControl from "@/pages/admin/WhatsAppControl";
import MomentsStudio from "@/pages/admin/MomentsStudio";import Establishments from "@/pages/admin/Establishments";import GroupDashboard from "@/pages/admin/GroupDashboard";import OneLedger from "@/pages/finance/OneLedger";
import UnifiedApprovals from "@/pages/UnifiedApprovals";
import OneApprovals from "@/pages/OneApprovals";
import CrmCustomersList from "@/pages/commercial/vtiger/CrmCustomersList";
import CrmCustomer360 from "@/pages/commercial/vtiger/CrmCustomer360";
import CommunicationsStudio from "@/pages/admin/CommunicationsStudio";
import IntegrationReadiness from "@/pages/admin/IntegrationReadiness";
import PdfStylePanel from "@/pages/admin/PdfStylePanel";
import OneRelationships from "@/pages/myone/OneRelationships";
import SecurityCentre from "@/pages/admin/SecurityCentre";
import AccountSecurity from "@/pages/account/AccountSecurity";
import PublicReferenceSources from "@/pages/admin/PublicReferenceSources";
import { HomeSignalWidgets } from "@/components/app/HomeSignalWidgets";
import MasterPage from "@/pages/admin/MasterPage";
import CustomerSites from "@/pages/admin/CustomerSites";
import CourierMaster from "@/pages/admin/CourierMaster";
import LocationInventory from "@/pages/admin/LocationInventory";
import OfficeBatches from "@/pages/OfficeBatches";
import CatalogueV2 from "@/pages/catalogue/CatalogueV2";import CatalogueWorkbench from "@/pages/catalogue/CatalogueWorkbench";import BackupRecovery from "@/pages/admin/BackupRecovery";import TestDataGuard from "@/pages/admin/TestDataGuard";import SubsidiaryReconcile from "@/pages/admin/SubsidiaryReconcile";import CatalogueDataQuality from "@/pages/catalogue/CatalogueDataQuality";import HsnEnrichment from "@/pages/technology/HsnEnrichment";import PartNumberEnrichment from "@/pages/technology/PartNumberEnrichment";import ColourEnrichment from "@/pages/technology/ColourEnrichment";import CoverageCentre from "@/pages/technology/CoverageCentre";import DescriptionEnrichment from "@/pages/technology/DescriptionEnrichment";import SpecConflicts from "@/pages/technology/SpecConflicts";import CatalogueCollections from "@/pages/catalogue/CatalogueCollections";import CatalogueEnquiries from "@/pages/catalogue/CatalogueEnquiries";import CataloguePublic from "@/pages/CataloguePublic";
import PlatformHealth from "@/pages/PlatformHealth";
import Renewals from "@/pages/Renewals";
import Buybacks from "@/pages/commercial/Buyback";
import QuoteAdmin from "@/pages/commercial/QuoteAdmin";
import RecoveryShadowBilling from "@/pages/commercial/RecoveryShadowBilling";
import RecoveryControlTower from "@/pages/operations/RecoveryControlTower";
import RecoverySetupWizard from "@/pages/customer360/RecoverySetupWizard";
import CommercialConsole from "@/pages/commercial/CommercialConsole";
import ContractCentre from "@/pages/commercial/ContractCentre";
import BillingCentre from "@/pages/commercial/BillingCentre";
import ShadowBilling from "@/pages/commercial/ShadowBilling";
import Profitability from "@/pages/commercial/Profitability";
import PreflightLedger from "@/pages/PreflightLedger";
import OneIntake from "@/pages/intake/OneIntake";
import OneMobile from "@/pages/mobile/OneMobile";
import WarehouseScan from "@/pages/mobile/WarehouseScan";
import WarehouseHome from "@/pages/mobile/WarehouseHome";
import WarehouseInward from "@/pages/mobile/WarehouseInward";
import ImplementationCentre from "@/pages/implementation/ImplementationCentre";
import Benchmarks from "@/pages/insights/Benchmarks";
import PartnerPortal from "@/pages/portal/PartnerPortal";
import Security from "@/pages/security/Security";
import Trust from "@/pages/trust/Trust";
import Knowledge from "@/pages/knowledge/Knowledge";
import SopCentre from "@/pages/knowledge/SopCentre";
import Automation from "@/pages/automation/Automation";
import WorkOne from "@/pages/work/WorkOne";
import Readiness from "@/pages/hardening/Readiness";
import Pilot from "@/pages/pilot/Pilot";
import PilotControlRoom from "@/pages/pilot/PilotControlRoom";
import Scale from "@/pages/scale/Scale";
import LaunchReadiness from "@/pages/launch/LaunchReadiness";
import LegalAgreementsAdmin from "@/pages/admin/LegalAgreementsAdmin";
import PolicyPage from "@/pages/public/PolicyPage";
import OnboardTenantWizard from "@/pages/admin/OnboardTenantWizard";
import ReacceptancePage from "@/pages/legal/ReacceptancePage";
import LegalAcceptanceGate from "@/components/app/LegalAcceptanceGate";
import ForceChangePasswordGate from "@/components/app/ForceChangePasswordGate";
import SsoConfigs from "@/pages/admin/SsoConfigs";
import PreflightApprovals from "@/pages/admin/PreflightApprovals";
import StubContracts from "@/pages/commercial/StubContracts";
import ExecutivePulse from "@/pages/insights/ExecutivePulse";
import { SupportInbox, SupportCase } from "@/pages/support/SupportInbox";
import ProductionReadiness from "@/pages/admin/ProductionReadiness";
import OneConnect from "@/pages/admin/OneConnect";
import OneConnectFree from "@/pages/admin/OneConnectFree";
import TenantBranding from "@/pages/admin/TenantBranding";
import WelcomeLabelStudio from "@/pages/admin/WelcomeLabelStudio";
import TranslationsAdmin from "@/pages/admin/TranslationsAdmin";
import WelcomeLabelBranding from "@/pages/settings/WelcomeLabelBranding";
import RiskIntelligence from "@/pages/insights/RiskIntelligence";
import PlatformFoundation from "@/pages/admin/PlatformFoundation";
import CRMDashboard from "@/pages/commercial/vtiger/CRMDashboard";import CrmPipelineBoard from "@/pages/commercial/vtiger/CrmPipelineBoard";
import QuotationsList from "@/pages/commercial/vtiger/QuotationsList";
import LegacyQuoteRedirect from "@/components/app/LegacyQuoteRedirect";
import HelpPage from "@/pages/public/HelpPage";
import SalesWorkspace, { SalesDrafts } from "@/pages/sales/SalesWorkspace";
import CustomerPOList from "@/pages/commercial/vtiger/CustomerPOList";
import VendorPOList from "@/pages/commercial/vtiger/VendorPOList";
import SalesOrdersList from "@/pages/commercial/vtiger/SalesOrdersList";
import OrderReadyDashboard from "@/pages/commercial/vtiger/OrderReadyDashboard";
import CustomerPOIntakePage from "@/pages/commercial/CustomerPOIntakePage";
import OneOrdersPage from "@/pages/commercial/OneOrdersPage";
import OrderWorkspace from "@/pages/commercial/OrderWorkspace";
import Device360 from "@/pages/devices/Device360";
import WaitingOnCustomerPage from "@/pages/customer/CustomerActions";
import CustomerActionRequired from "@/pages/operations/CustomerActionRequired";
import PeopleStockLoop from "@/pages/lifecycle/PeopleStockLoop";
import ControlLayer from "@/pages/control/ControlLayer";
import CustomerPortal from "@/pages/portal/CustomerPortal";
import PayPortal from "@/pages/public/PayPortal";
import EmployeeOnboardingPage from "@/pages/portal/EmployeeOnboarding";
import ReplenishmentPage from "@/pages/portal/Replenishment";
import EmployeeOffboardingPage from "@/pages/portal/EmployeeOffboarding";
import AssetRecoveryPage from "@/pages/portal/AssetRecovery";
import ServiceCentre from "@/pages/servicecentre/ServiceCentre";
import WarehouseDeviceStatus from "@/pages/warehouse/WarehouseDeviceStatus";
import StockScan from "@/pages/warehouse/StockScan";
import ReturnsWorkspace from "@/pages/warehouse/ReturnsWorkspace";
import MyCustodyPage from "@/components/returns/CustodyView";
import TaskManager from "@/pages/tasks/TaskManager";
import CustomerCatalogue from "@/pages/catalogue/CustomerCatalogue";
import CustomerCatalogueAdmin from "@/pages/admin/CustomerCatalogueAdmin";
import ServiceConsole from "@/pages/admin/ServiceConsole";
import SuntronixAI from "@/pages/ai/SuntronixAI";
import DeploymentInbox from "@/pages/deployments/DeploymentInbox";
import AddressConfirmPublic from "@/pages/public/AddressConfirmPublic";
import OrderFeedbackPublic from "@/pages/public/OrderFeedbackPublic";
import InspectionReportPublic from "@/pages/public/InspectionReportPublic";
import AppleCtoMaster from "@/pages/admin/AppleCtoMaster";
import EmailHealth from "@/pages/admin/EmailHealth";
import PublicRegister from "@/pages/public/PublicRegister";
import Employee360 from "@/pages/people/Employee360";
import SmartFulfilment from "@/pages/orders/SmartFulfilment";
import RelationshipsDashboard from "@/pages/relationships/RelationshipsDashboard";
import Person360 from "@/pages/relationships/Person360";
import Segments from "@/pages/relationships/Segments";
import Journeys from "@/pages/relationships/Journeys";
import Outreach from "@/pages/outreach/Outreach";
import OutreachCampaign from "@/pages/outreach/Campaign";
import OrderCommandCentre from "@/pages/commercial/OrderCommandCentre";
import FulfilmentPlanWorkspace from "@/pages/logistics/FulfilmentPlanWorkspace";
import MultiLocationView from "@/pages/logistics/MultiLocationView";
import SerialRegister from "@/pages/logistics/SerialRegister";
import WaEntryPublic from "@/pages/public/WaEntryPublic";
import ContactsHub from "@/pages/contacts/ContactsHub";
import FulfilmentDispatch from "@/pages/logistics/FulfilmentDispatch";
import DeliveryChallans from "@/pages/dispatch/DeliveryChallans";
import EmployeePortal from "@/pages/employee/EmployeePortal";
import VerifyAddressPublic from "@/pages/public/VerifyAddressPublic";
import ServiceConfirmPublic from "@/pages/public/ServiceConfirmPublic";
import ServiceFeedbackPublic from "@/pages/public/ServiceFeedbackPublic";
import VerifyDeliveryPublic from "@/pages/public/VerifyDeliveryPublic";
import CommercialOverviewPage from "@/pages/commercial/CommercialOverviewPage";
import SalesDesk from "@/pages/sales/SalesDesk";
import TechLibrary from "@/pages/technology/TechLibrary";
import TaxReports from "@/pages/finance/TaxReports";
import KycVault from "@/pages/admin/KycVault";
import GoLiveReport from "@/pages/admin/GoLiveReport";
import ServiceMaster from "@/pages/technology/ServiceMaster";
import ConfigStudio from "@/pages/admin/ConfigStudio";import DocumentTemplates from "@/pages/admin/DocumentTemplates";import LoginExperience from "@/pages/admin/LoginExperience";import TermsConditions from "@/pages/admin/TermsConditions";import QuotationTemplate from "@/pages/admin/QuotationTemplate";import BankDetails from "@/pages/admin/BankDetails";import CustomerPrefixCleanup from "@/pages/admin/CustomerPrefixCleanup";import CrmAutomation from "@/pages/admin/CrmAutomation";
import ProcessStudio from "@/pages/admin/ProcessStudio";
import OneStudio from "@/pages/admin/OneStudio";
import RuleStudio from "@/pages/admin/RuleStudio";
import FollowUpPage from "@/pages/commercial/FollowUp";
import WalkinFeedbackPage from "@/pages/sales/WalkinFeedbackPage";
import SupplierWorkspace from "@/pages/supplier/SupplierWorkspace";
import MyApprovals from "@/pages/approvals/MyApprovals";
import ContractsWorkspace from "@/pages/commercial/ContractsWorkspace";
import RenewalsPage from "@/pages/commercial/RenewalsPage";
import AppleConfigurator from "@/pages/sales/AppleConfigurator";import AppleServices from "@/pages/operations/AppleServices";import AppleCareControl from "@/pages/operations/AppleCareControl";import AppleCareRequests from "@/pages/operations/AppleCareRequests";import AppleCarePublicPortal from "@/pages/AppleCarePublicPortal";import SmartAttach from "@/pages/commercial/SmartAttach";import OneEngage from "@/pages/commercial/OneEngage";import WhatsAppCampaigns from "@/pages/commercial/WhatsAppCampaigns";import SmartAttachPublic from "@/pages/SmartAttachPublic";import StockInventory from "@/pages/operations/StockInventory";import EmployeeFulfilment from "@/pages/operations/EmployeeFulfilment";import { MorningStockBrief } from "@/pages/operations/StockInventoryIntel";import MySettings from "@/pages/profile/MySettings";import OneToolbox from "@/pages/work/OneToolbox";import QuickQuoteLauncher from "@/components/app/QuickQuoteLauncher";import DailyStock from "@/pages/operations/DailyStock";import CashControl from "@/pages/finance/CashControl";import CardControl from "@/pages/finance/CardControl";import CardReports from "@/pages/finance/CardReports";import OnePost from "@/pages/finance/OnePost";import CustomerOwnedStock from "@/pages/operations/CustomerOwnedStock";
import OpportunitiesList from "@/pages/commercial/vtiger/OpportunitiesList";
import ContactReconcile from "@/pages/commercial/vtiger/ContactReconcile";
import CompanyProfile from "@/pages/admin/CompanyProfile";
import QuoteDisclaimer from "@/pages/admin/QuoteDisclaimer";
import OneNetwork from "@/pages/network/OneNetwork";
import EmailTemplates from "@/pages/network/EmailTemplates";
import AdminHonorifics from "@/pages/admin/AdminHonorifics";
import Typography from "@/pages/admin/Typography";
import PrivacyDpdp from "@/pages/admin/PrivacyDpdp";
import CustomerOnboardingAdmin from "@/pages/admin/CustomerOnboardingAdmin";
import CustomerOnboardingPublic from "@/pages/public/CustomerOnboardingPublic";
import EmailConfirmPublic from "@/pages/public/EmailConfirmPublic";
import PrivacyRequestPublic from "@/pages/public/PrivacyRequestPublic";
const SocialStudio = lazy(() => import("@/pages/marketing/SocialStudio"));
import ProductMaster from "@/pages/technology/ProductMaster";
import OemProductIntelligence from "@/pages/technology/ProductIntelligence";
import AppleIndiaSync from "@/pages/technology/AppleIndiaSync";
import ImageEnrichment from "@/pages/technology/ImageEnrichment";
import AiImageStudio from "@/pages/technology/AiImageStudio";
import OneTelephony from "@/pages/telephony/OneTelephony";
import FieldService from "@/pages/operations/FieldService";
import AmcPms from "@/pages/commercial/AmcPms";
import OurContracts from "@/pages/customer360/OurContracts";
import OurRma from "@/pages/customer360/OurRma";
import OurSubscriptions from "@/pages/customer360/OurSubscriptions";
import RmaControlCentre from "@/pages/service/RmaControlCentre";
import Subscriptions from "@/pages/commercial/Subscriptions";
import CsatCentre from "@/pages/commercial/CsatCentre";
import OurFeedback from "@/pages/customer360/OurFeedback";
import CsatPublic from "@/pages/feedback/CsatPublic";
import LegacyFeedbackCatchup from "@/pages/admin/LegacyFeedbackCatchup";
import AddressVerifiedNotices from "@/pages/admin/AddressVerifiedNotices";
import ServiceConfirmationAdmin from "@/pages/admin/ServiceConfirmationAdmin";
import ServicesPending from "@/pages/operations/ServicesPending";
import OrderAcceptanceInbox from "@/pages/commercial/OrderAcceptanceInbox";
import OmnichannelIntake from "@/pages/admin/OmnichannelIntake";
import TallyConnector from "@/pages/admin/TallyConnector";
import OneConnect3 from "@/pages/admin/OneConnect3";
import AppleBusiness from "@/pages/admin/AppleBusiness";
import AuthorisedSigners from "@/pages/admin/AuthorisedSigners";
import OneFiles from "@/pages/files/OneFiles";
import OneForms from "@/pages/forms/OneForms";
import OneFileEditor from "@/pages/files/OneFileEditor";
import ComplianceBridge from "@/pages/finance/ComplianceBridge";
import IndiaTaxConnect from "@/pages/finance/IndiaTaxConnect";
import IdentityAccess from "@/pages/tenant/IdentityAccess";
import WarrantyImporter from "@/pages/technology/WarrantyImporter";
import TallyReconciliation from "@/pages/finance/TallyReconciliation";
import ProjectsWorkspace from "@/pages/work/ProjectsWorkspace";
import TelephonyConfig from "@/pages/admin/TelephonyConfig";
import AssetAgeCentre from "@/pages/technology/AssetAgeCentre";
import QuotationsDashboard from "@/pages/commercial/QuotationsDashboard";
import { Discovery as OpDiscovery, TechnologyGraph as OpGraph, PolicyStudio as OpPolicy } from "@/pages/one-plus";
import {
  Operations as InsOperations, Technology as InsTechnology,
  Deployment as InsDeployment, Recovery as InsRecovery,
  Support as InsSupport, Inventory as InsInventory, Reuse as InsReuse,
  Commercial as InsCommercial, Billing as InsBilling, Courier as InsCourier,
  CustomerHealth as InsCustomerHealth, SlaAnalytics as InsSla,
  DataQuality as InsDQ, Catalogue as InsCatalogue, Reports as InsReports,
} from "@/pages/insights/stubs";
import { customerPathAllowed, isCustomerRole } from "@/constants/customerNav";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="animate-pulse text-slate-500 text-sm">Loading Suntronix ONE…</div>
      </div>
    );
  }
  if (!user) return <LoginRedirect />;
  return (
    <>
      {["read_only", "auditor"].includes(user.role) && (
        <div data-testid="view-only-banner" className="fixed bottom-2 left-1/2 -translate-x-1/2 z-50 rounded-full bg-slate-900/90 text-white text-xs px-3 py-1 pointer-events-none">
          View-only access · changes are disabled
        </div>
      )}
      {children}
      {user.requires_legal_acceptance && <LegalAcceptanceGate />}
      {!user.requires_legal_acceptance && <ForceChangePasswordGate />}
    </>
  );
}

function LegacyOverview() {
  const { user } = useAuth();
  const QUOTE_ROLES = ["super_admin", "management", "sales_coordinator", "operations", "sales", "account_manager"];
  const showQuickQuote = user && QUOTE_ROLES.includes(user.role);
  return (<><Dashboard /><div className="max-w-[1500px] mx-auto px-4 pb-4 space-y-4">{showQuickQuote && <QuickQuoteLauncher />}<HomeSignalWidgets /><MorningStockBrief /></div></>);
}

function HomeSwitch() {
  const { user } = useAuth();
  const isCustomer = user && isCustomerRole(user.role);
  const [landing, setLanding] = useState(() => window.__oneTodayLanding || null);
  useEffect(() => {
    if (!user || isCustomer || window.__oneTodayLanding) return;
    api.get("/one-today/landing")
      .then(({ data }) => {
        // Only an explicit user preference bypasses the OS Home.
        const v = data.source === "user" ? data.landing : "enterprise";
        window.__oneTodayLanding = v; setLanding(v);
      })
      .catch(() => { window.__oneTodayLanding = "enterprise"; setLanding("enterprise"); });
  }, [user, isCustomer]);
  if (isCustomer) return <CustomerHome />;
  if (!landing) return null;
  if (landing === "enterprise") return <CommandCentreHome />;
  if (landing === "os") return <OsHome />;
  if (landing === "overview") return <LegacyOverview />;
  return <ActionCentre />;
}

function LoginRedirect() {
  const { pathname, search } = useLocation();
  const next = pathname && pathname !== "/" ? `?next=${encodeURIComponent(pathname + (search || ""))}` : "";
  return <Navigate to={`/login${next}`} replace />;
}

function DocumentsRedirect() {
  const { user } = useAuth();
  const external = user && (isCustomerRole(user.role) || String(user.role || "").startsWith("supplier"));
  return <Navigate to={external ? "/our-technology" : "/vault"} replace />;
}

function SuntronixOnly({ children }) {
  const { user } = useAuth();
  // Only Suntronix internal users may reach these tools. Gate on role, not on
  // master_company_id — some internal accounts carry a stray company link and
  // must NOT be locked out. Customer and supplier accounts are redirected home.
  if (!user) return <Navigate to="/" replace />;
  const isExternal = isCustomerRole(user.role) || String(user.role || "").startsWith("supplier");
  if (isExternal) return <Navigate to="/" replace />;
  return children;
}

/** ShellPicker — reads localStorage("st.shell") and mounts either the classic
 *  sidebar layout (AppShell) or the new ONE Spaces layout (SpacesShell).  This
 *  lets management flip the whole platform to Spaces without a redeploy while
 *  keeping the classic layout available for comparison. */
function ShellPicker() {
  // Enterprise OS Shell is the default for internal users. Customer tenants
  // stay on ONE Spaces. Spaces/Classic remain switchable via st.shell.
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isCustomer = user && isCustomerRole(user.role);
  const isExternal = isCustomer || String(user?.role || "").startsWith("supplier");
  if (isExternal && pathname.startsWith("/admin")) return <AccessDenied />;
  if (isCustomer && !customerPathAllowed(user.role, pathname)) return <AccessDenied />;
  let choice = "os";
  try { choice = window.localStorage.getItem("st.shell") || "os"; } catch { /* ignore */ }
  if (isCustomer) return choice === "classic" ? <AppShell /> : <SpacesShell />;
  if (choice === "classic") return <AppShell />;
  if (choice === "spaces") return <SpacesShell />;
  return <OsShell />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <LocaleProvider>
      <PreviewProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/pay/:token" element={<PayPortal />} />
          <Route path="/login/legacy" element={<LoginLegacy />} />
          <Route path="/login/preview" element={<LoginPreview />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ForgotPassword />} />
          <Route path="/partner-portal" element={<PartnerPortal />} />
          <Route path="/e/confirm/:token" element={<PublicDeliveryConfirm />} />
          <Route path="/address-confirm/:token" element={<AddressConfirmPublic />} />
          <Route path="/order-feedback/:token" element={<OrderFeedbackPublic />} />
          <Route path="/inspection-report/:token" element={<InspectionReportPublic />} />
          <Route path="/r/:token" element={<PublicRegister />} />
          <Route path="/wa/:slug" element={<WaEntryPublic />} />
          <Route path="/me" element={<EmployeePortal />} />
          <Route path="/verify-address/:token" element={<VerifyAddressPublic />} />
          <Route path="/service-confirm/:token" element={<ServiceConfirmPublic />} />
          <Route path="/service-feedback/:token" element={<ServiceFeedbackPublic />} />
          <Route path="/verify-delivery/:token" element={<VerifyDeliveryPublic />} />
          <Route path="/accept-quote/:token" element={<PublicQuoteAccept />} />
          <Route path="/q/:token" element={<DigitalQuote />} />
          <Route path="/request-hub" element={<RequestHubLanding />} />
          <Route path="/product-intelligence/:token" element={<PublicProductIntelligence />} />
          <Route path="/f/:token" element={<PublicOneForm />} />
          <Route path="/e/feedback/:token" element={<PublicFeedback />} />
          <Route path="/feedback/csat/:token" element={<CsatPublic />} />
          <Route path="/legal/reaccept/:token" element={<ReacceptancePage />} />
          <Route path="/customer-onboarding" element={<CustomerOnboardingPublic />} />
          <Route path="/customer-onboarding/verify/:token" element={<EmailConfirmPublic kind="onboarding" />} />
          <Route path="/privacy-request" element={<PrivacyRequestPublic />} />
          <Route path="/privacy-request/verify/:token" element={<EmailConfirmPublic kind="privacy" />} />
          <Route path="/privacy" element={<PolicyPage kind="privacy" />} />
          <Route path="/terms" element={<PolicyPage kind="terms" />} />
          <Route path="/supplier" element={<SupplierWorkspace />} />
          <Route path="/supplier/po/:poId" element={<SupplierWorkspace />} />
          <Route path="/applecare/:token" element={<AppleCarePublicPortal />} />
          <Route path="/smart-attach/:token" element={<SmartAttachPublic />} />
          <Route path="/c/:token" element={<CataloguePublic />} />
          <Route path="/vault/s/:token" element={<VaultSharePublic />} />
          <Route path="/pv/s/:token" element={<PrivateVaultShare />} />
          <Route
            element={
              <Protected>
                <ShellPicker />
              </Protected>
            }
          >
            <Route path="/" element={<HomeSwitch />} />
            <Route path="/preview/navigation" element={<NavigationPreview />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/requests" element={<Requests />} />
            <Route path="/deployments/inbox" element={<SuntronixOnly><DeploymentInbox /></SuntronixOnly>} />
            <Route path="/sales/workspace" element={<SuntronixOnly><SalesWorkspace /></SuntronixOnly>} />
            <Route path="/sales/workspace/drafts" element={<SuntronixOnly><SalesDrafts /></SuntronixOnly>} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/deploy" element={<DeployDevice />} />
            <Route path="/our-stock" element={<OurStockAtSuntronix />} />
            <Route path="/offboarding" element={<Offboarding />} />
            <Route path="/shipments" element={<Shipments />} />
            <Route path="/pickups" element={<ReversePickup />} />
            <Route path="/warehouse" element={<Warehouse />} />
            <Route path="/inspection" element={<Inspection />} />
            <Route path="/data-erasure" element={<DataErasure />} />
            <Route path="/itad" element={<ITAD />} />
            <Route path="/service" element={<Service />} />
            <Route path="/employees" element={<Employees />} />
            <Route path="/employees/:employeeId" element={<EmployeeDetail />} />
            <Route path="/companies" element={<Companies />} />
            <Route path="/companies/:masterId" element={<CompanySubsidiaries />} />
            <Route path="/admin/legal" element={<LegalAgreementsAdmin />} />
            <Route path="/admin/backup-recovery" element={<SuntronixOnly><BackupRecovery /></SuntronixOnly>} />
            <Route path="/admin/platform-health" element={<SuntronixOnly><PlatformHealth /></SuntronixOnly>} />
            <Route path="/admin/test-data" element={<SuntronixOnly><TestDataGuard /></SuntronixOnly>} />
            <Route path="/admin/subsidiary-reconcile" element={<SuntronixOnly><SubsidiaryReconcile /></SuntronixOnly>} />
            <Route path="/admin/onboard-tenant" element={<OnboardTenantWizard />} />
            <Route path="/admin/sso" element={<SuntronixOnly><SsoConfigs /></SuntronixOnly>} />
            <Route path="/admin/preflight-approvals" element={<PreflightApprovals />} />
            <Route path="/commercial/contracts/stubs" element={<StubContracts />} />
            <Route path="/insights"                   element={<ExecutivePulse />} />
            <Route path="/insights/operations"        element={<InsOperations />} />
            <Route path="/insights/technology"        element={<InsTechnology />} />
            <Route path="/insights/deployment"        element={<InsDeployment />} />
            <Route path="/insights/recovery"          element={<InsRecovery />} />
            <Route path="/insights/support"           element={<InsSupport />} />
            <Route path="/insights/inventory"         element={<InsInventory />} />
            <Route path="/insights/reuse"             element={<InsReuse />} />
            <Route path="/insights/commercial"        element={<InsCommercial />} />
            <Route path="/insights/billing"           element={<InsBilling />} />
            <Route path="/insights/courier"           element={<InsCourier />} />
            <Route path="/insights/customer-health"   element={<InsCustomerHealth />} />
            <Route path="/insights/sla"               element={<InsSla />} />
            <Route path="/insights/data-quality"      element={<InsDQ />} />
            <Route path="/insights/catalogue"         element={<InsCatalogue />} />
            <Route path="/insights/reports"           element={<InsReports />} />
            <Route path="/admin/imports" element={<ImportHistory />} />
            <Route path="/admin/audit/force-overrides" element={<ForceOverrides />} />
            <Route path="/approvals-inbox" element={<ApprovalsInbox />} />
            <Route path="/scan-asset" element={<ScanAsset />} />
            <Route path="/catalogue" element={<Catalogue />} />
            <Route path="/tenant-home" element={<TenantHome />} />
            <Route path="/tenant-catalogue" element={<TenantCatalogue />} />
            <Route path="/my-quotes" element={<TenantMyQuotes />} />
            <Route path="/my-orders" element={<TenantMyOrders />} />
            <Route path="/portal/request-hub" element={<RequestHubPage />} />
            <Route path="/operations/request-hub" element={<SuntronixOnly><RequestHubPage internal /></SuntronixOnly>} />
            <Route path="/my-tenant-assets" element={<TenantMyAssets />} />
            <Route path="/my-invoices" element={<TenantMyInvoices />} />
            <Route path="/my-locations" element={<TenantMyLocations />} />
            <Route path="/admin/tenant-catalogue" element={<SuntronixOnly><TenantCatalogueAdmin /></SuntronixOnly>} />
            <Route path="/services" element={<ServiceCatalogue />} />
            <Route path="/services/group/:group" element={<ServiceGroup />} />
            <Route path="/services/detail/:code" element={<ServiceDetail />} />
            <Route path="/services/bundle/:code" element={<ServiceBundle />} />
            <Route path="/services/request/:code" element={<ServiceRequestWizard />} />
            <Route path="/admin/services/analytics" element={<SuntronixOnly><ServiceAnalytics /></SuntronixOnly>} />
            <Route path="/users" element={<Users />} />
            <Route path="/audit" element={<SuntronixOnly><AuditTrail /></SuntronixOnly>} />
            <Route path="/audit/legacy" element={<Audit />} />
            <Route path="/exceptions" element={<Navigate to="/exception-centre" replace />} />
            <Route path="/exceptions/legacy" element={<Exceptions />} />
            <Route path="/exception-centre" element={<SuntronixOnly><ExceptionCentre /></SuntronixOnly>} />
            <Route path="/notifications" element={<NotificationCentrePage />} />
            <Route path="/command-centre" element={<SuntronixOnly><CommandCentre /></SuntronixOnly>} />
            <Route path="/admin/integrations" element={<SuntronixOnly><IntegrationCentre /></SuntronixOnly>} />
            <Route path="/admin/integration-centre" element={<SuntronixOnly><IntegrationCentre /></SuntronixOnly>} />
            <Route path="/admin/integrations/hub" element={<SuntronixOnly><IntegrationHub /></SuntronixOnly>} />
            <Route path="/admin/integrations/whatsapp" element={<SuntronixOnly><WhatsAppIntegration /></SuntronixOnly>} />
            <Route path="/contacts/relationships" element={<SuntronixOnly><ContactsHub /></SuntronixOnly>} />
            <Route path="/logistics/fulfilment" element={<SuntronixOnly><FulfilmentDispatch /></SuntronixOnly>} />
            <Route path="/dispatch/delivery-challans" element={<SuntronixOnly><DeliveryChallans /></SuntronixOnly>} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/imports" element={<ImportCentre />} />
            <Route path="/refresh-forecast" element={<RefreshForecast />} />
            <Route path="/my-devices" element={<MyDevices />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/discovery" element={<DiscoveryInbox />} />
            <Route path="/discrepancies" element={<Discrepancies />} />
            <Route path="/duplicates" element={<Duplicates />} />
            <Route path="/normalization" element={<Normalization />} />
            <Route path="/product-intelligence" element={<ProductIntelligence />} />
            <Route path="/data-quality" element={<DataQuality />} />
            <Route path="/admin/data-quality" element={<SuntronixOnly><DataQualityControl /></SuntronixOnly>} />
            <Route path="/admin/catalogue-masters" element={<SuntronixOnly><CatalogueMasters /></SuntronixOnly>} />
            <Route path="/admin/catalogue-cleanup" element={<SuntronixOnly><CatalogueCleanup /></SuntronixOnly>} />
            <Route path="/admin/product-classification" element={<SuntronixOnly><ProductClassification /></SuntronixOnly>} />
            <Route path="/admin/mapping-review" element={<SuntronixOnly><MappingReview /></SuntronixOnly>} />
            <Route path="/admin/legacy-systems" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/quote-disclaimer" element={<SuntronixOnly><QuoteDisclaimer /></SuntronixOnly>} />
            <Route path="/admin/honorifics" element={<SuntronixOnly><AdminHonorifics /></SuntronixOnly>} />
            <Route path="/admin/typography" element={<SuntronixOnly><Typography /></SuntronixOnly>} />
            <Route path="/admin/privacy-dpdp" element={<SuntronixOnly><PrivacyDpdp /></SuntronixOnly>} />
            <Route path="/admin/customer-onboarding" element={<SuntronixOnly><CustomerOnboardingAdmin /></SuntronixOnly>} />
            <Route path="/marketing/social-media" element={<SuntronixOnly><Suspense fallback={<div className="p-6 text-sm text-slate-500">Loading Social Media…</div>}><SocialStudio /></Suspense></SuntronixOnly>} />
            <Route path="/operations/multibrand-stock" element={<SuntronixOnly><MultiBrandStock /></SuntronixOnly>} />
            <Route path="/admin/one-connect/product-content" element={<SuntronixOnly><ProductContentProviders /></SuntronixOnly>} />
            <Route path="/technology/product-content" element={<SuntronixOnly><ProductContentHub /></SuntronixOnly>} />
            <Route path="/my-orders" element={<MyOrders />} />
            <Route path="/admin/exceptions" element={<SuntronixOnly><AdminExceptions /></SuntronixOnly>} />
            <Route path="/admin/integration-health" element={<SuntronixOnly><IntegrationHealth /></SuntronixOnly>} />
            <Route path="/admin/system-health" element={<SuntronixOnly><SystemHealth /></SuntronixOnly>} />
            <Route path="/admin/failure-centre" element={<SuntronixOnly><FailureCentre /></SuntronixOnly>} />
            <Route path="/admin/automation-studio" element={<SuntronixOnly><AutomationStudio /></SuntronixOnly>} />
            <Route path="/admin/atlas/flow-definitions" element={<SuntronixOnly><FlowDefinitions /></SuntronixOnly>} />
            <Route path="/admin/security/vault" element={<SuntronixOnly><SecureVault /></SuntronixOnly>} />
            <Route path="/compliance/kyc" element={<SuntronixOnly><KycEngine /></SuntronixOnly>} />
            <Route path="/compliance/kyc/readiness" element={<SuntronixOnly><KycReadiness /></SuntronixOnly>} />
            <Route path="/finance/whatsapp-followup" element={<SuntronixOnly><ArWhatsAppDesk /></SuntronixOnly>} />
            <Route path="/admin/demo-console" element={<SuntronixOnly><DemoConsole /></SuntronixOnly>} />
            <Route path="/admin/tenant-readiness" element={<SuntronixOnly><TenantReadiness /></SuntronixOnly>} />
            <Route path="/admin/preflight-rules" element={<SuntronixOnly><PreflightRules /></SuntronixOnly>} />
            <Route path="/invoices" element={<InvoiceCentre />} />
            <Route path="/finance/order-closure" element={<SuntronixOnly><OrderClosure /></SuntronixOnly>} />
            <Route path="/reports/dc-added-items" element={<SuntronixOnly><AddedItemsReport /></SuntronixOnly>} />
            <Route path="/finance/invoice-copies" element={<SuntronixOnly><InvoiceCopies /></SuntronixOnly>} />
            <Route path="/commercial/contracts" element={<ContractCentre />} />
            <Route path="/commercial/billing" element={<BillingCentre />} />
            <Route path="/commercial/billing/shadow" element={<ShadowBilling />} />
            <Route path="/commercial/profitability" element={<Profitability />} />
            <Route path="/commercial/buybacks" element={<Buybacks />} />
            <Route path="/commercial/quotes" element={<QuoteAdmin />} />
            <Route path="/commercial/crm" element={<SuntronixOnly><CRMDashboard /></SuntronixOnly>} />
            <Route path="/commercial/crm/opportunities" element={<SuntronixOnly><OpportunitiesList /></SuntronixOnly>} />
            <Route path="/commercial/crm/pipeline" element={<SuntronixOnly><CrmPipelineBoard /></SuntronixOnly>} />
            <Route path="/commercial/crm/customer-import" element={<Navigate to="/commercial/crm/customers" replace />} />
            <Route path="/commercial/crm/contact-reconcile" element={<SuntronixOnly><ContactReconcile /></SuntronixOnly>} />
            <Route path="/admin/company-profile" element={<SuntronixOnly><CompanyProfile /></SuntronixOnly>} />
            <Route path="/admin/legacy-feedback" element={<SuntronixOnly><LegacyFeedbackCatchup /></SuntronixOnly>} />
            <Route path="/admin/address-verified-notices" element={<SuntronixOnly><AddressVerifiedNotices /></SuntronixOnly>} />
            <Route path="/admin/service-confirmation" element={<SuntronixOnly><ServiceConfirmationAdmin /></SuntronixOnly>} />
            <Route path="/operations/services-pending" element={<SuntronixOnly><ServicesPending /></SuntronixOnly>} />
            <Route path="/commercial/crm/quotations" element={<SuntronixOnly><QuotationsList /></SuntronixOnly>} />
            <Route path="/commercial/acceptance-inbox" element={<SuntronixOnly><OrderAcceptanceInbox /></SuntronixOnly>} />
            <Route path="/commercial/crm/quotations/:qid" element={<SuntronixOnly><LegacyQuoteRedirect /></SuntronixOnly>} />
            <Route path="/commercial/crm/customer-pos" element={<SuntronixOnly><CustomerPOList /></SuntronixOnly>} />
            <Route path="/commercial/customer-po" element={<SuntronixOnly><CustomerPOIntakePage /></SuntronixOnly>} />
            <Route path="/commercial/one-orders" element={<SuntronixOnly><OneOrdersPage /></SuntronixOnly>} />
            <Route path="/orders/command-centre" element={<SuntronixOnly><OrderCommandCentre /></SuntronixOnly>} />
            <Route path="/logistics/fulfilment-plan/:oid" element={<SuntronixOnly><FulfilmentPlanWorkspace /></SuntronixOnly>} />
            <Route path="/logistics/trace" element={<SuntronixOnly><MultiLocationView /></SuntronixOnly>} />
            <Route path="/logistics/serial-register/:oid" element={<SuntronixOnly><SerialRegister /></SuntronixOnly>} />
            <Route path="/people/employee-360" element={<SuntronixOnly><Employee360 /></SuntronixOnly>} />
            <Route path="/orders/smart" element={<SuntronixOnly><SmartFulfilment /></SuntronixOnly>} />
            <Route path="/orders/smart/:oid" element={<SuntronixOnly><SmartFulfilment /></SuntronixOnly>} />
            <Route path="/one/relationships" element={<SuntronixOnly><RelationshipsDashboard /></SuntronixOnly>} />
            <Route path="/one/relationships/people/:cid" element={<SuntronixOnly><Person360 /></SuntronixOnly>} />
            <Route path="/one/relationships/segments" element={<SuntronixOnly><Segments /></SuntronixOnly>} />
            <Route path="/one/relationships/journeys" element={<SuntronixOnly><Journeys /></SuntronixOnly>} />
            <Route path="/one/outreach" element={<SuntronixOnly><Outreach /></SuntronixOnly>} />
            <Route path="/one/outreach/campaigns/:cid" element={<SuntronixOnly><OutreachCampaign /></SuntronixOnly>} />
            <Route path="/people/employee-360/:key" element={<SuntronixOnly><Employee360 /></SuntronixOnly>} />
            <Route path="/orders/:oid" element={<SuntronixOnly><OrderWorkspace /></SuntronixOnly>} />
            <Route path="/devices" element={<Device360 />} />
            <Route path="/waiting-on-customer" element={<SuntronixOnly><CustomerActionRequired /></SuntronixOnly>} />
            <Route path="/customer-action-required" element={<SuntronixOnly><CustomerActionRequired /></SuntronixOnly>} />
            <Route path="/waiting-on-customer/legacy" element={<SuntronixOnly><WaitingOnCustomerPage /></SuntronixOnly>} />
            <Route path="/lifecycle/people" element={<PeopleStockLoop />} />
            <Route path="/control" element={<ControlLayer />} />
            <Route path="/portal" element={<CustomerPortal />} />
            <Route path="/portal/onboarding" element={<EmployeeOnboardingPage />} />
            <Route path="/portal/onboarding/:oid" element={<EmployeeOnboardingPage />} />
            <Route path="/lifecycle/onboarding" element={<SuntronixOnly><EmployeeOnboardingPage /></SuntronixOnly>} />
            <Route path="/lifecycle/onboarding/:oid" element={<SuntronixOnly><EmployeeOnboardingPage /></SuntronixOnly>} />
            <Route path="/lifecycle/replenishment" element={<SuntronixOnly><ReplenishmentPage /></SuntronixOnly>} />
            <Route path="/portal/offboarding" element={<EmployeeOffboardingPage />} />
            <Route path="/portal/offboarding/:oid" element={<EmployeeOffboardingPage />} />
            <Route path="/lifecycle/offboarding" element={<SuntronixOnly><EmployeeOffboardingPage /></SuntronixOnly>} />
            <Route path="/lifecycle/offboarding/:oid" element={<SuntronixOnly><EmployeeOffboardingPage /></SuntronixOnly>} />
            <Route path="/portal/asset-recovery" element={<AssetRecoveryPage />} />
            <Route path="/portal/asset-recovery/:rid" element={<AssetRecoveryPage />} />
            <Route path="/operations/asset-recovery" element={<SuntronixOnly><AssetRecoveryPage /></SuntronixOnly>} />
            <Route path="/operations/asset-recovery/:rid" element={<SuntronixOnly><AssetRecoveryPage /></SuntronixOnly>} />
            <Route path="/service-centre" element={<SuntronixOnly><ServiceCentre /></SuntronixOnly>} />
            <Route path="/service-centre/:section" element={<SuntronixOnly><ServiceCentre /></SuntronixOnly>} />
            <Route path="/warehouse/device-status" element={<SuntronixOnly><WarehouseDeviceStatus /></SuntronixOnly>} />
            <Route path="/warehouse/scan/:code?" element={<SuntronixOnly><StockScan /></SuntronixOnly>} />
            <Route path="/warehouse/returns" element={<SuntronixOnly><ReturnsWorkspace /></SuntronixOnly>} />
            <Route path="/assets/custody" element={<MyCustodyPage />} />
            <Route path="/tasks" element={<SuntronixOnly><TaskManager /></SuntronixOnly>} />
            <Route path="/catalogue/my" element={<CustomerCatalogue />} />
            <Route path="/admin/customer-catalogues" element={<SuntronixOnly><CustomerCatalogueAdmin /></SuntronixOnly>} />
            <Route path="/admin/api-layer" element={<SuntronixOnly><ServiceConsole /></SuntronixOnly>} />
            <Route path="/devices/:serial" element={<Device360 />} />
            <Route path="/commercial/overview" element={<SuntronixOnly><CommercialOverviewPage /></SuntronixOnly>} />
            <Route path="/sales-desk" element={<SuntronixOnly><SalesDesk /></SuntronixOnly>} />
            <Route path="/sales" element={<Navigate to="/sales-desk" replace />} />
            <Route path="/commercial/crm/sales-orders" element={<SuntronixOnly><SalesOrdersList /></SuntronixOnly>} />
            <Route path="/commercial/crm/order-ready" element={<SuntronixOnly><OrderReadyDashboard /></SuntronixOnly>} />
            <Route path="/commercial/crm/vendor-pos" element={<SuntronixOnly><VendorPOList /></SuntronixOnly>} />
            <Route path="/commercial/crm/settings" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/commercial/vtiger/settings" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/commercial/recovery-shadow-billing" element={<RecoveryShadowBilling />} />
            <Route path="/operations/recover/control-tower" element={<RecoveryControlTower />} />
            <Route path="/customer/:mcId/recovery-setup" element={<RecoverySetupWizard />} />
            <Route path="/preflight/ledger" element={<PreflightLedger />} />
            <Route path="/advisories" element={<Advisories />} />
            <Route path="/inventory-audit" element={<InventoryAudit />} />
            <Route path="/source-priority" element={<SourcePriority />} />
            <Route path="/reservations" element={<Reservations />} />
            <Route path="/loaners" element={<Loaners />} />
            <Route path="/rma" element={<RMA />} />
            <Route path="/warranty" element={<Warranty />} />
            <Route path="/support" element={<SupportInbox />} />
            <Route path="/support/:caseId" element={<SupportCase />} />
            <Route path="/admin/production-readiness" element={<ProductionReadiness />} />
            <Route path="/admin/one-connect" element={<OneConnect />} />
            <Route path="/admin/one-connect/free" element={<OneConnectFree />} />
            <Route path="/admin/one-connect/vtiger/*" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/sync-health" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/quote-sample" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/quote-fields" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/accounts-fields" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/products-fields" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/purchaseorder-fields" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/vendors-fields" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/live-console" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/technology/product-master" element={<SuntronixOnly><ProductMaster /></SuntronixOnly>} />
            <Route path="/admin/product-intelligence/data-sources/apple-cto" element={<SuntronixOnly><AppleCtoMaster /></SuntronixOnly>} />
            <Route path="/admin/communications/email-health" element={<SuntronixOnly><EmailHealth /></SuntronixOnly>} />
            <Route path="/technology/product-intelligence" element={<SuntronixOnly><OemProductIntelligence /></SuntronixOnly>} />
            <Route path="/technology/apple-india" element={<SuntronixOnly><AppleIndiaSync /></SuntronixOnly>} />
            <Route path="/technology/image-enrichment" element={<SuntronixOnly><ImageEnrichment /></SuntronixOnly>} />
            <Route path="/technology/image-studio" element={<SuntronixOnly><AiImageStudio /></SuntronixOnly>} />
            <Route path="/technology/asset-age-centre" element={<AssetAgeCentre />} />
            <Route path="/technology/tech-library" element={<SuntronixOnly><TechLibrary /></SuntronixOnly>} />
            <Route path="/finance/tax-reports" element={<SuntronixOnly><TaxReports /></SuntronixOnly>} />
            <Route path="/administration/corporate-kyc" element={<SuntronixOnly><KycVault /></SuntronixOnly>} />
            <Route path="/admin/corporate-kyc" element={<SuntronixOnly><KycVault /></SuntronixOnly>} />
            <Route path="/administration/go-live-report" element={<SuntronixOnly><GoLiveReport /></SuntronixOnly>} />
            <Route path="/admin/go-live-report" element={<SuntronixOnly><GoLiveReport /></SuntronixOnly>} />
            <Route path="/technology/service-master" element={<SuntronixOnly><ServiceMaster /></SuntronixOnly>} />
            <Route path="/commercial/service-master" element={<SuntronixOnly><ServiceMaster /></SuntronixOnly>} />
            <Route path="/administration/config-studio" element={<SuntronixOnly><ConfigStudio /></SuntronixOnly>} />
            <Route path="/administration/one-studio" element={<SuntronixOnly><OneStudio /></SuntronixOnly>} />
            <Route path="/admin/one-studio" element={<SuntronixOnly><OneStudio /></SuntronixOnly>} />
            <Route path="/admin/config-studio" element={<SuntronixOnly><ConfigStudio /></SuntronixOnly>} />
            <Route path="/admin/document-templates" element={<SuntronixOnly><DocumentTemplates /></SuntronixOnly>} />
            <Route path="/admin/login-experience" element={<SuntronixOnly><LoginExperience /></SuntronixOnly>} />
            <Route path="/admin/crm-automation" element={<SuntronixOnly><CrmAutomation /></SuntronixOnly>} />
            <Route path="/administration/document-templates" element={<SuntronixOnly><DocumentTemplates /></SuntronixOnly>} />
            <Route path="/admin/terms-conditions" element={<SuntronixOnly><TermsConditions /></SuntronixOnly>} />
            <Route path="/admin/quotation-template" element={<SuntronixOnly><QuotationTemplate /></SuntronixOnly>} />
            <Route path="/admin/bank-details" element={<SuntronixOnly><BankDetails /></SuntronixOnly>} />
            <Route path="/admin/customer-prefix-cleanup" element={<SuntronixOnly><CustomerPrefixCleanup /></SuntronixOnly>} />
            <Route path="/administration/process-studio" element={<SuntronixOnly><ProcessStudio /></SuntronixOnly>} />
            <Route path="/admin/process-studio" element={<SuntronixOnly><ProcessStudio /></SuntronixOnly>} />
            <Route path="/commercial/followup" element={<SuntronixOnly><FollowUpPage /></SuntronixOnly>} />
            <Route path="/sales/walkin-feedback" element={<SuntronixOnly><WalkinFeedbackPage /></SuntronixOnly>} />
            <Route path="/sales-desk/followup" element={<SuntronixOnly><FollowUpPage /></SuntronixOnly>} />
            <Route path="/administration/rules" element={<SuntronixOnly><RuleStudio /></SuntronixOnly>} />
            <Route path="/administration/approvals" element={<SuntronixOnly><MyApprovals /></SuntronixOnly>} />
            <Route path="/commercial/entitlements" element={<SuntronixOnly><ContractsWorkspace /></SuntronixOnly>} />
            <Route path="/commercial/entitlements/renewals" element={<SuntronixOnly><RenewalsPage /></SuntronixOnly>} />
            <Route path="/sales-desk/apple-configurator" element={<SuntronixOnly><AppleConfigurator /></SuntronixOnly>} />
            <Route path="/sales/apple" element={<SuntronixOnly><AppleConfigurator /></SuntronixOnly>} />
            <Route path="/operations/apple-services" element={<SuntronixOnly><AppleServices /></SuntronixOnly>} />
            <Route path="/operations/applecare-plus" element={<SuntronixOnly><AppleCareControl /></SuntronixOnly>} />
            <Route path="/toolbox" element={<SuntronixOnly><OneToolbox /></SuntronixOnly>} />
            <Route path="/operations/applecare-requests" element={<SuntronixOnly><AppleCareRequests /></SuntronixOnly>} />
            <Route path="/commercial/smart-attach" element={<SuntronixOnly><SmartAttach /></SuntronixOnly>} />
            <Route path="/commercial/one-engage" element={<SuntronixOnly><OneEngage /></SuntronixOnly>} />            <Route path="/commercial/whatsapp-campaigns" element={<SuntronixOnly><WhatsAppCampaigns /></SuntronixOnly>} />            <Route path="/operations/stock-inventory" element={<SuntronixOnly><StockInventory /></SuntronixOnly>} />
            <Route path="/operations/customer-owned-stock" element={<SuntronixOnly><CustomerOwnedStock /></SuntronixOnly>} />
            <Route path="/operations/daily-stock" element={<SuntronixOnly><DailyStock /></SuntronixOnly>} />
            <Route path="/finance/cash-control" element={<SuntronixOnly><CashControl /></SuntronixOnly>} />
            <Route path="/finance/cards" element={<SuntronixOnly><CardControl /></SuntronixOnly>} />
            <Route path="/finance/card-reports" element={<SuntronixOnly><CardReports /></SuntronixOnly>} />
            <Route path="/operations/employee-fulfilment" element={<SuntronixOnly><EmployeeFulfilment /></SuntronixOnly>} />
            <Route path="/services/apple" element={<SuntronixOnly><AppleServices /></SuntronixOnly>} />
            <Route path="/profile/my-settings" element={<MySettings />} />
            <Route path="/commercial/quotations" element={<SuntronixOnly><QuotationsDashboard /></SuntronixOnly>} />
            <Route path="/commercial/sales-quotations" element={<SuntronixOnly><QuotationsDashboard /></SuntronixOnly>} />
            <Route path="/admin/one-connect/vtiger/discovery" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/vtiger/mappings" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/one-connect/edge" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/admin/tenant-branding" element={<TenantBranding />} />
            <Route path="/admin/welcome-labels" element={<SuntronixOnly><WelcomeLabelStudio /></SuntronixOnly>} />
            <Route path="/admin/translations" element={<SuntronixOnly><TranslationsAdmin /></SuntronixOnly>} />
            <Route path="/settings/welcome-label-branding" element={<WelcomeLabelBranding />} />
            <Route path="/insights/security/risk-intelligence" element={<RiskIntelligence />} />
            <Route path="/admin/platform-foundation" element={<PlatformFoundation />} />
            <Route path="/warranty-nudges" element={<WarrantyNudges />} />
            <Route path="/refresh-engine" element={<RefreshEngine />} />
            <Route path="/help" element={<HelpCentre />} />
            <Route path="/help/trust" element={<TrustCentre />} />
            <Route path="/help/accessibility" element={<AccessibilityStatement />} />
            <Route path="/help/status" element={<SystemStatus />} />
            <Route path="/help/legal" element={<Navigate to="/help/trust" replace />} />
            <Route path="/executive" element={<ExecutiveHome />} />
            <Route path="/concierge" element={<Concierge />} />
            <Route path="/concierge/plan/:planId" element={<Concierge />} />
            <Route path="/whats-new" element={<WhatsNew />} />
            <Route path="/notices" element={<Notices />} />
            <Route path="/feedback" element={<ProductFeedback />} />
            <Route path="/feedback/legacy" element={<Feedback />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/governance" element={<GovernanceConsole />} />
            <Route path="/governance-centre" element={<GovernanceCentre />} />
            <Route path="/control-tower" element={<ControlTower />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/admin/marketplace" element={<SuntronixOnly><MarketplaceAdmin /></SuntronixOnly>} />
            <Route path="/partners" element={<PartnerNetwork />} />
            <Route path="/trade-partners" element={<SuntronixOnly><PartnerList /></SuntronixOnly>} />
            <Route path="/scenario-lab" element={<ScenarioLab />} />
            <Route path="/economics" element={<Economics />} />
            <Route path="/circularity" element={<Circularity />} />
            <Route path="/admin/studio" element={<SuntronixOnly><Studio /></SuntronixOnly>} />
            <Route path="/security" element={<Security />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/knowledge" element={<Knowledge />} />
            <Route path="/sop" element={<SopCentre />} />
            <Route path="/sop/:key" element={<SopCentre />} />
            <Route path="/automation" element={<SuntronixOnly><Automation /></SuntronixOnly>} />
            <Route path="/work" element={<WorkOne />} />
            <Route path="/network" element={<OneNetwork />} />
            <Route path="/network/templates" element={<EmailTemplates />} />
            <Route path="/today" element={<SuntronixOnly><ActionCentre /></SuntronixOnly>} />
            <Route path="/action-centre" element={<SuntronixOnly><ActionCentre /></SuntronixOnly>} />
            <Route path="/today/classic" element={<SuntronixOnly><OneToday /></SuntronixOnly>} />
            <Route path="/home" element={<SuntronixOnly><CommandCentreHome /></SuntronixOnly>} />
            <Route path="/brand/visual-dna" element={<SuntronixOnly><VisualDNA /></SuntronixOnly>} />
            <Route path="/home/classic" element={<SuntronixOnly><EnterpriseHome /></SuntronixOnly>} />
            <Route path="/home/legacy" element={<SuntronixOnly><OsHome /></SuntronixOnly>} />
            <Route path="/dashboard" element={<SuntronixOnly><RoleDashboard /></SuntronixOnly>} />
            <Route path="/customer-360/:id" element={<SuntronixOnly><Customer360 /></SuntronixOnly>} />
            <Route path="/lifecycle/:kind/:id" element={<LifecyclePage />} />
            <Route path="/copilot" element={<CopilotPage />} />
            <Route path="/ai" element={<SuntronixAI />} />
            <Route path="/admin/role-dashboards" element={<SuntronixOnly><DashboardAdmin /></SuntronixOnly>} />
            <Route path="/admin/automation" element={<SuntronixOnly><AutomationEngine /></SuntronixOnly>} />
            <Route path="/overview" element={<SuntronixOnly><LegacyOverview /></SuntronixOnly>} />
            <Route path="/one-ai" element={<SuntronixOnly><OneAiWorkspace /></SuntronixOnly>} />
            <Route path="/service-desk" element={<SuntronixOnly><OneServiceDesk mode="desk" /></SuntronixOnly>} />
            <Route path="/support/my-requests" element={<OneServiceDesk mode="portal" />} />
            <Route path="/employee-hub" element={<EmployeeHub />} />
            <Route path="/service-hub" element={<ServiceHub />} />
            <Route path="/our-technology" element={<CommandCentreHub />} />
            <Route path="/settings/experience" element={<ExperienceSettings />} />
            <Route path="/admin/moments-studio" element={<SuntronixOnly><MomentsStudio /></SuntronixOnly>} />
            <Route path="/admin/establishments" element={<SuntronixOnly><Establishments /></SuntronixOnly>} />
            <Route path="/admin/group-dashboard" element={<SuntronixOnly><GroupDashboard /></SuntronixOnly>} />
            <Route path="/finance/one-ledger" element={<SuntronixOnly><OneLedger defaultScope="BUSINESS" /></SuntronixOnly>} />
            <Route path="/one-post" element={<SuntronixOnly><OnePost /></SuntronixOnly>} />
            <Route path="/ledger" element={<OneLedger defaultScope="PERSONAL" />} />
            <Route path="/work/approvals" element={<UnifiedApprovals />} />
            <Route path="/approvals/one" element={<OneApprovals />} />
            <Route path="/commercial/crm/customers" element={<SuntronixOnly><CrmCustomersList /></SuntronixOnly>} />
            <Route path="/commercial/crm/customers/:id" element={<SuntronixOnly><CrmCustomer360 /></SuntronixOnly>} />
            <Route path="/admin/comms" element={<Navigate to="/admin/communications" replace />} />
            <Route path="/admin/communications" element={<SuntronixOnly><CommunicationsStudio /></SuntronixOnly>} />
            <Route path="/admin/whatsapp-control" element={<SuntronixOnly><WhatsAppControl /></SuntronixOnly>} />
            <Route path="/vault" element={<SuntronixOnly><OneDocumentVault /></SuntronixOnly>} />
            <Route path="/vault/inbox" element={<SuntronixOnly><OneInbox /></SuntronixOnly>} />
            <Route path="/private-vault" element={<SuntronixOnly><PrivateVault /></SuntronixOnly>} />
            <Route path="/warranty-intel" element={<WarrantyIntelligence />} />
            <Route path="/cio" element={<CioCommandCenter />} />
            <Route path="/ask-ai" element={<AskAiPage />} />
            <Route path="/alerts" element={<AlertCentre />} />
            <Route path="/ai-recommendations" element={<AiRecommendations />} />
            <Route path="/alerts/policies" element={<AlertPolicies />} />
            <Route path="/aiops" element={<AiOps />} />
            <Route path="/aiops/:tab" element={<AiOps />} />
            <Route path="/inbox" element={<Navigate to="/vault/inbox" replace />} />
            <Route path="/admin/one-connect/email-intake" element={<SuntronixOnly><OneInbox /></SuntronixOnly>} />
            <Route path="/admin/one-control-centre" element={<SuntronixOnly><OneControlCentre /></SuntronixOnly>} />
            <Route path="/documents" element={<DocumentsRedirect />} />
            <Route path="/admin/integration-readiness" element={<SuntronixOnly><IntegrationReadiness /></SuntronixOnly>} />
            <Route path="/admin/pdf-style" element={<SuntronixOnly><PdfStylePanel /></SuntronixOnly>} />
            <Route path="/readiness" element={<SuntronixOnly><Readiness /></SuntronixOnly>} />
            <Route path="/pilot" element={<SuntronixOnly><Pilot /></SuntronixOnly>} />
            <Route path="/pilot/control-room" element={<SuntronixOnly><PilotControlRoom /></SuntronixOnly>} />
            <Route path="/scale" element={<SuntronixOnly><Scale /></SuntronixOnly>} />
            <Route path="/launch" element={<SuntronixOnly><LaunchReadiness /></SuntronixOnly>} />
            <Route path="/location-master" element={<LocationMaster />} />
            <Route path="/admin/control-centre" element={<SuntronixOnly><AdminControlCentre /></SuntronixOnly>} />
            <Route path="/admin/locked-accounts" element={<SuntronixOnly><LockedAccounts /></SuntronixOnly>} />
            <Route path="/admin/one-sms" element={<SuntronixOnly><OneSms /></SuntronixOnly>} />
            <Route path="/telephony" element={<SuntronixOnly><OneTelephony /></SuntronixOnly>} />
            <Route path="/operations/field-service" element={<SuntronixOnly><FieldService /></SuntronixOnly>} />
            <Route path="/commercial/amc-pms" element={<SuntronixOnly><AmcPms /></SuntronixOnly>} />
            <Route path="/commercial/csat" element={<SuntronixOnly><CsatCentre /></SuntronixOnly>} />
            <Route path="/our-contracts" element={<OurContracts />} />
            <Route path="/our-rma" element={<OurRma />} />
            <Route path="/our-subscriptions" element={<OurSubscriptions />} />
            <Route path="/service/rma" element={<SuntronixOnly><RmaControlCentre /></SuntronixOnly>} />
            <Route path="/commercial/subscriptions" element={<SuntronixOnly><Subscriptions /></SuntronixOnly>} />
            <Route path="/our-feedback" element={<OurFeedback />} />
            <Route path="/admin/one-connect/telephony" element={<SuntronixOnly><TelephonyConfig /></SuntronixOnly>} />
            <Route path="/admin/one-connect/intake" element={<SuntronixOnly><OmnichannelIntake /></SuntronixOnly>} />
            <Route path="/admin/one-connect/tally" element={<SuntronixOnly><TallyConnector /></SuntronixOnly>} />
            <Route path="/admin/one-connect/control-centre" element={<SuntronixOnly><OneConnect3 /></SuntronixOnly>} />
            <Route path="/admin/one-connect/apple-business" element={<SuntronixOnly><AppleBusiness /></SuntronixOnly>} />
            <Route path="/admin/documents/authorised-signers" element={<SuntronixOnly><AuthorisedSigners /></SuntronixOnly>} />
            <Route path="/files" element={<SuntronixOnly><OneFiles /></SuntronixOnly>} />
            <Route path="/files/editor/:id" element={<SuntronixOnly><OneFileEditor /></SuntronixOnly>} />
            <Route path="/forms/one-forms" element={<SuntronixOnly><OneForms /></SuntronixOnly>} />
            <Route path="/finance/compliance-bridge" element={<SuntronixOnly><ComplianceBridge /></SuntronixOnly>} />
            <Route path="/admin/one-connect/india-tax" element={<SuntronixOnly><IndiaTaxConnect /></SuntronixOnly>} />
            <Route path="/tenant/identity-access" element={<SuntronixOnly><IdentityAccess /></SuntronixOnly>} />
            <Route path="/technology/warranty-importer" element={<SuntronixOnly><WarrantyImporter /></SuntronixOnly>} />
            <Route path="/finance/tally-reconciliation" element={<SuntronixOnly><TallyReconciliation /></SuntronixOnly>} />
            <Route path="/work/projects" element={<SuntronixOnly><ProjectsWorkspace /></SuntronixOnly>} />
            <Route path="/admin/finance-vault" element={<SuntronixOnly><FinanceVault /></SuntronixOnly>} />
            <Route path="/continuity" element={<SuntronixOnly><OneContinuity /></SuntronixOnly>} />
            <Route path="/relationships" element={<SuntronixOnly><OneRelationships /></SuntronixOnly>} />
            <Route path="/admin/security-centre" element={<SuntronixOnly><SecurityCentre /></SuntronixOnly>} />
            <Route path="/account/security" element={<AccountSecurity />} />
            <Route path="/admin/public-reference" element={<SuntronixOnly><PublicReferenceSources /></SuntronixOnly>} />
            <Route path="/admin/masters/:name" element={<MasterPage />} />
            <Route path="/admin/sites" element={<CustomerSites />} />
            <Route path="/admin/couriers" element={<CourierMaster />} />
            <Route path="/admin/location-inventory" element={<LocationInventory />} />
            <Route path="/operations/office-batches" element={<OfficeBatches />} />
            <Route path="/catalogue-v2" element={<CatalogueV2 />} />
            <Route path="/catalogue-workbench" element={<SuntronixOnly><CatalogueWorkbench /></SuntronixOnly>} />
            <Route path="/technology/product-catalogue" element={<CatalogueV2 />} />
            <Route path="/sales-desk/browse-catalogue" element={<CatalogueV2 />} />
            <Route path="/technology/catalogue-data-quality" element={<CatalogueDataQuality />} />
            <Route path="/technology/hsn-enrichment" element={<SuntronixOnly><HsnEnrichment /></SuntronixOnly>} />
            <Route path="/tools/hsn-format" element={<SuntronixOnly><HsnFormatTool /></SuntronixOnly>} />
            <Route path="/tools/image-format" element={<SuntronixOnly><ImageFormatTool /></SuntronixOnly>} />
            <Route path="/tools/product-format-centre" element={<SuntronixOnly><ProductFormatCentre /></SuntronixOnly>} />
            <Route path="/commercial/apple-cto" element={<SuntronixOnly><AppleCTO /></SuntronixOnly>} />
            <Route path="/technology/part-number-enrichment" element={<SuntronixOnly><PartNumberEnrichment /></SuntronixOnly>} />
            <Route path="/technology/colour-enrichment" element={<SuntronixOnly><ColourEnrichment /></SuntronixOnly>} />
            <Route path="/technology/coverage-centre" element={<SuntronixOnly><CoverageCentre /></SuntronixOnly>} />
            <Route path="/technology/description-enrichment" element={<SuntronixOnly><DescriptionEnrichment /></SuntronixOnly>} />
            <Route path="/technology/spec-conflicts" element={<SuntronixOnly><SpecConflicts /></SuntronixOnly>} />
            <Route path="/sales-desk/collections" element={<CatalogueCollections />} />
            <Route path="/sales-desk/catalogue-enquiries" element={<CatalogueEnquiries />} />
            <Route path="/technology/catalogue-collections" element={<CatalogueCollections />} />
            <Route path="/renewals" element={<Renewals />} />
            <Route path="/commercial" element={<CommercialConsole />} />
            <Route path="/intake" element={<OneIntake />} />
            <Route path="/mobile" element={<OneMobile />} />
            <Route path="/mobile/warehouse" element={<SuntronixOnly><WarehouseHome /></SuntronixOnly>} />
            <Route path="/mobile/warehouse-scan" element={<SuntronixOnly><WarehouseScan /></SuntronixOnly>} />
            <Route path="/mobile/warehouse-inward" element={<SuntronixOnly><WarehouseInward /></SuntronixOnly>} />
            <Route path="/implementation" element={<ImplementationCentre />} />
            <Route path="/benchmarks" element={<Benchmarks />} />
            <Route path="/conversations" element={<Conversations />} />
            <Route path="/mentions" element={<Mentions />} />
            <Route path="/inventory" element={<MyInventory />} />
            <Route path="/inventory/inward" element={<BulkInward />} />
            <Route path="/inventory/passport/:assetId" element={<DevicePassport />} />
            <Route path="/customers" element={<MasterCustomersList />} />
            <Route path="/customers/:masterClientId" element={<MasterCustomer360 />} />
            <Route path="/customers/all/flat" element={<CustomerMatrix />} />
            <Route path="/customer/new" element={<SuntronixOnly><OnboardingWizard /></SuntronixOnly>} />
            <Route path="/customer/new/:draftId" element={<SuntronixOnly><OnboardingWizard /></SuntronixOnly>} />
            <Route path="/customer/:mcId/tech-env" element={<TechnologyEnvironment />} />
            <Route path="/customer/:mcId/tax" element={<TaxRegistrationsEditor />} />
            <Route path="/customers/:masterClientId/contracts" element={<ContractsEditor />} />
            <Route path="/customers/:masterClientId/logistics" element={<LogisticsConsole />} />
            <Route path="/bulk-deployment" element={<BulkDeployment />} />
            <Route path="/bulk-deployment/:batchId" element={<BulkDeployment />} />
            <Route path="/deployment-centre" element={<DeploymentControlCentre />} />
            <Route path="/deployments/:requestId/pre-acceptance" element={<PreAcceptanceDelivery />} />
            <Route path="/cases/:caseId" element={<DeploymentCase />} />
            <Route path="/account/mfa" element={<MFASettings />} />
            <Route path="/master-portfolio" element={<MasterClientPortfolio />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <WaComposerHost />
      <Toaster position="top-right" offset={96} richColors closeButton />
      </PreviewProvider>
      </LocaleProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
